import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import axios from 'axios'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import toast from 'react-hot-toast'
import { useTrader } from 'context/TraderTypeContext'
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SearchIcon } from 'components/svg'
import GetUrls from 'ts/enums/Url'
import { DailySalesReportResponse } from 'ts/types/SalesReport'

type TableCell = {
  count: number
  total: number
}

type PresetRange = {
  label: string
  range: () => { start: string; end: string }
}

const formatCurrency = (value: number) =>
  value.toLocaleString('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 2
  })

const formatDisplayDate = (value: string) => {
  const date = new Date(value)
  if (isNaN(date.getTime())) {
    return 'Unknown date'
  }
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

type DateRangePopoverProps = {
  isOpen: boolean
  presetRanges: PresetRange[]
  baseMonth: Date
  setBaseMonth: (date: Date) => void
  startDate: string
  endDate: string
  onDayClick: (day: string) => void
  setRange: (start: string, end: string) => void
  formatDisplayDateProp: (value: string) => string
  onClose: () => void
  onSearch: () => void
  isSameDay: (a: string, b: string) => boolean
  isBetween: (value: string, start: string, end: string) => boolean
  getMonthMatrix: (offset: number) => { weeks: (string | null)[][]; title: string }
}

const DateRangePopover: FC<DateRangePopoverProps> = ({
  isOpen,
  presetRanges,
  baseMonth,
  setBaseMonth,
  startDate,
  endDate,
  onDayClick,
  setRange,
  formatDisplayDateProp,
  onClose,
  onSearch,
  isSameDay,
  isBetween,
  getMonthMatrix
}) => {
  if (!isOpen) return null

  const { weeks, title } = getMonthMatrix(0)

  return (
    <div
      className="absolute z-20 mt-2 rounded-lg border border-gray-200 bg-white shadow-xl"
      style={{ width: 'min(530px, 96vw)' }}
    >
      <div className="grid gap-4 p-4" style={{ gridTemplateColumns: '140px 1fr' }}>
        <div className="border-r border-gray-200 pr-2">
          <div className="space-y-1">
            {presetRanges.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const next = preset.range()
                  setRange(next.start, next.end)
                  setBaseMonth(new Date(next.start))
                }}
                className="w-full rounded px-3 py-2 text-sm text-left text-gray-700 hover:bg-indigo-50"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                const next = new Date(baseMonth)
                next.setMonth(baseMonth.getMonth() - 1)
                setBaseMonth(next)
              }}
              className="rounded border border-gray-200 p-2 hover:bg-gray-50"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <div className="text-sm font-semibold text-gray-800 text-center min-w-[140px]">
              {title}
            </div>
            <button
              type="button"
              onClick={() => {
                const next = new Date(baseMonth)
                next.setMonth(baseMonth.getMonth() + 1)
                setBaseMonth(next)
              }}
              className="rounded border border-gray-200 p-2 hover:bg-gray-50"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="border border-gray-100 rounded-lg p-3 !mt-1">
            <div className="grid grid-cols-7 text-[11px] font-semibold text-gray-500 mb-1">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                <div key={d} className="text-center py-1">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 text-sm">
              {weeks.flatMap((week, weekIdx) =>
                week.map((day, dayIdx) => {
                  if (!day) {
                    return <div key={`${weekIdx}-${dayIdx}`} />
                  }

                  const isStart = isSameDay(day, startDate)
                  const isEnd = isSameDay(day, endDate)
                  const inRange = startDate && endDate && !isStart && !isEnd && isBetween(day, startDate, endDate)

                  const className = [
                    'text-center py-2 rounded cursor-pointer border',
                    'hover:border-indigo-300 hover:bg-indigo-50',
                    isStart || isEnd ? 'bg-indigo-100 font-bold border-indigo-300 text-indigo-800' : '',
                    inRange ? 'bg-indigo-50 border-indigo-100 text-indigo-800' : '',
                    !isStart && !isEnd && !inRange ? 'border-transparent text-gray-800' : ''
                  ].join(' ')

                  return (
                    <button
                      key={`${weekIdx}-${dayIdx}`}
                      type="button"
                      onClick={() => onDayClick(day)}
                      className={className}
                    >
                      {Number(day.split('-')[2])}
                    </button>
                  )
                })
              )}
            </div>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>
              Start: <span className="font-semibold">{formatDisplayDateProp(startDate)}</span>
            </span>
            <span>
              End: <span className="font-semibold">{formatDisplayDateProp(endDate)}</span>
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                onClose()
                onSearch()
              }}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded bg-indigo-600 text-sm text-white hover:bg-indigo-700"
            >
              <SearchIcon className="h-4 w-4" style={{ transform: 'translateY(-3px)' }} />
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const getLocalDateString = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

const getYesterdayLocalDateString = () => {
  const date = new Date()
  date.setDate(date.getDate() - 1)
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

const SalesReportPage = () => {
  const Url = GetUrls()
  const { traderId } = useTrader()

  const initialStartDateRef = useRef(getYesterdayLocalDateString())
  const initialEndDateRef = useRef(getLocalDateString())

  const [startDate, setStartDate] = useState(() => initialStartDateRef.current)
  const [endDate, setEndDate] = useState(() => initialEndDateRef.current)
  const [isRangeOpen, setIsRangeOpen] = useState(false)
  const rangePickerRef = useRef<HTMLDivElement | null>(null)
  const [baseMonth, setBaseMonth] = useState(() => {
    const d = new Date(initialStartDateRef.current)
    d.setDate(1)
    return d
  })
  const [report, setReport] = useState<DailySalesReportResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const productUsers = useMemo(() => {
    const set = new Set<string>()
    report?.productDetails.forEach((detail) => set.add(detail.userName))
    return Array.from(set)
  }, [report?.productDetails])

  const paymentUsers = useMemo(() => {
    const set = new Set<string>()
    report?.paymentDetails.forEach((detail) => set.add(detail.userName))
    return Array.from(set)
  }, [report?.paymentDetails])

  const products = useMemo(() => {
    const set = new Set<string>()
    report?.productDetails.forEach((detail) => set.add(detail.productName))
    return Array.from(set)
  }, [report?.productDetails])

  const paymentTypes = useMemo(() => {
    const set = new Set<string>()
    report?.paymentDetails.forEach((detail) => set.add(detail.paymentType))
    return Array.from(set)
  }, [report?.paymentDetails])

  const getProductCell = useCallback(
    (productName: string, userName: string): TableCell => {
      const entry = report?.productDetails.find(
        (detail) =>
          detail.productName === productName && detail.userName === userName
      )

      return {
        count: entry?.countPerProduct ?? 0,
        total: entry?.salesPerProduct ?? 0
      }
    },
    [report?.productDetails]
  )

  const getPaymentCell = useCallback(
    (paymentType: string, userName: string) => {
      const entry = report?.paymentDetails.find(
        (detail) =>
          detail.paymentType === paymentType && detail.userName === userName
      )

      return {
        total: entry?.paymentValue ?? 0
      }
    },
    [report?.paymentDetails]
  )

  const getProductTotals = useCallback(
    (productName: string): TableCell => {
      const entry = report?.productDetails.find(
        (detail) => detail.productName === productName
      )

      return {
        count: entry?.totalCountPerProduct ?? 0,
        total: entry?.totalSalesPerProduct ?? 0
      }
    },
    [report?.productDetails]
  )

  const productUserTotals = useMemo(() => {
    const totals: Record<string, TableCell> = {}

    productUsers.forEach((user) => {
      const entry = report?.productDetails.find(
        (detail) => detail.userName === user
      )

      totals[user] = {
        count: entry?.totalCountPerUser ?? 0,
        total: entry?.totalSalesPerUser ?? 0
      }
    })

    return totals
  }, [productUsers, report?.productDetails])

  const paymentTotals = useMemo(() => {
    const totals: Record<string, number> = {}

    paymentTypes.forEach((type) => {
      const entry = report?.paymentDetails.find(
        (detail) => detail.paymentType === type
      )

      totals[type] = entry?.totalPaymentPerPaymentType ?? 0
    })

    return totals
  }, [paymentTypes, report?.paymentDetails])

  const toIsoStartOfDay = (dateValue?: string, fallback?: string) => {
    const fallbackDate = fallback || getLocalDateString()
    const datePart = dateValue?.split('T')[0] || fallbackDate

    return `${datePart}T00:00:00`
  }

  const toDate = (value: string) => new Date(`${value}T00:00:00`)

  const formatIso = (date: Date) => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const isSameDay = (a: string, b: string) => a === b

  const isBetween = (value: string, start: string, end: string) => {
    const d = toDate(value).getTime()
    return d >= toDate(start).getTime() && d <= toDate(end).getTime()
  }

  const getMonthMatrix = (monthOffset: number) => {
    const monthDate = new Date(baseMonth)
    monthDate.setMonth(baseMonth.getMonth() + monthOffset)

    const year = monthDate.getFullYear()
    const month = monthDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const startWeekday = firstDay.getDay() // 0 Sun - 6 Sat
    const daysInMonth = new Date(year, month + 1, 0).getDate()

    const days: (string | null)[] = []
    for (let i = 0; i < startWeekday; i += 1) {
      days.push(null)
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      days.push(formatIso(new Date(year, month, d)))
    }

    const weeks: (string | null)[][] = []
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }

    const title = `${monthDate.toLocaleString('default', {
      month: 'long'
    })} ${year}`

    return { weeks, title }
  }

  const buildRequestPayload = useCallback(
    (startValue?: string, endValue?: string) => {
      const numericTraderId = traderId ? Number(traderId) : null
      const start = startValue || initialStartDateRef.current
      const end = endValue || startValue || initialEndDateRef.current

      return {
        startDate: toIsoStartOfDay(start, initialStartDateRef.current),
        endDate: toIsoStartOfDay(end, initialEndDateRef.current),
        traderId: numericTraderId
      }
    },
    [traderId]
  )

  const fetchReport = useCallback(async (startValue?: string, endValue?: string) => {
    if (!traderId) {
      setError('A trader must be selected before viewing the report.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const { data } = await axios.post<DailySalesReportResponse>(
        Url.getDailySalesReport,
        buildRequestPayload(startValue, endValue)
      )

      if (data.outcomeCode !== '0') {
        throw new Error(data.outcomeUserMessage || data.outcomeMessage)
      }

      setReport(data)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load the daily sales report.'

      setReport(null)
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }, [buildRequestPayload, Url, traderId])
  useEffect(() => {
    fetchReport(initialStartDateRef.current, initialEndDateRef.current)
  }, [fetchReport])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isRangeOpen &&
        rangePickerRef.current &&
        !rangePickerRef.current.contains(event.target as Node)
      ) {
        setIsRangeOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isRangeOpen])

  const handleDayClick = useCallback(
    (day: string) => {
      if (!startDate || (startDate && endDate && !isSameDay(startDate, endDate))) {
        setStartDate(day)
        setEndDate(day)
        return
      }

      if (toDate(day).getTime() < toDate(startDate).getTime()) {
        setStartDate(day)
        return
      }

      setEndDate(day)
    },
    [startDate, endDate]
  )

  const handleRangeToggle = () => setIsRangeOpen((open) => !open)

  const handleSearch = useCallback(() => {
    fetchReport(startDate, endDate)
  }, [fetchReport, startDate, endDate])

  const displayDateRange = useMemo(
    () => `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`,
    [startDate, endDate]
  )

  const presetRanges = useMemo(
    () => [
      {
        label: 'Yesterday',
        range: () => {
          const end = new Date() // today
          const start = new Date()
          start.setDate(end.getDate() - 1) // yesterday
          return { start: formatIso(start), end: formatIso(end) }
        }
      },
      {
        label: 'Last 3 Days',
        range: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(end.getDate() - 2)
          return { start: formatIso(start), end: formatIso(end) }
        }
      },
      {
        label: 'Last 7 Days',
        range: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(end.getDate() - 6)
          return { start: formatIso(start), end: formatIso(end) }
        }
      },
      {
        label: 'Last 30 Days',
        range: () => {
          const end = new Date()
          const start = new Date()
          start.setDate(end.getDate() - 29)
          return { start: formatIso(start), end: formatIso(end) }
        }
      },
      {
        label: 'This Month',
        range: () => {
          const now = new Date()
          const start = new Date(now.getFullYear(), now.getMonth(), 1)
          const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
          return { start: formatIso(start), end: formatIso(end) }
        }
      },
      {
        label: 'Last Month',
        range: () => {
          const now = new Date()
          const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
          const end = new Date(now.getFullYear(), now.getMonth(), 0)
          return { start: formatIso(start), end: formatIso(end) }
        }
      }
    ],
    []
  )

  const handleExportPdf = () => {
    if (!report) {
      return
    }

    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text('Daily Sales Report', 14, 15)
    doc.setFontSize(10)
    doc.text(
      `Dates: ${displayDateRange} | Generated: ${new Date().toLocaleString()}`,
      14,
      22
    )

    const productHeadRow1 = [
      { content: 'Product / User', rowSpan: 2, styles: { halign: 'left' as const } },
      ...productUsers.map((user) => ({
        content: user,
        colSpan: 2,
        styles: { halign: 'center' as const }
      })),
      { content: 'Total', colSpan: 2, styles: { halign: 'center' as const } }
    ]

    const productHeadRow2 = [
      ...productUsers.flatMap(() => [
        { content: 'Qty', styles: { halign: 'center' as const } },
        { content: 'Total', styles: { halign: 'center' as const } }
      ]),
      { content: 'Qty', styles: { halign: 'center' as const } },
      { content: 'Total', styles: { halign: 'center' as const } }
    ]

    const productBody = products.map((product) => {
      const row = [
        product,
        ...productUsers.flatMap((user) => {
          const cell = getProductCell(product, user)
          return [
            cell.count ? cell.count.toString() : '-',
            cell.total ? formatCurrency(cell.total) : '-'
          ]
        })
      ]

      const totals = getProductTotals(product)
      row.push(
        totals.count ? totals.count.toString() : '-',
        totals.total ? formatCurrency(totals.total) : '-'
      )

      return row
    })

    const summaryRow = [
      'Total',
      ...productUsers.flatMap((user) => {
        const totals = productUserTotals[user]
        return [
          totals?.count ? totals.count.toString() : '-',
          totals?.total ? formatCurrency(totals.total) : '-'
        ]
      }),
      report.grandCount ? report.grandCount.toString() : '-',
      report.grandTotal ? formatCurrency(report.grandTotal) : '-'
    ]

    productBody.push(summaryRow)

    autoTable(doc, {
      head: [productHeadRow1, productHeadRow2],
      body: productBody,
      startY: 28,
      styles: {
        fontSize: 8,
        halign: 'center',
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: [180, 212, 231]
      },
      columnStyles: {
        0: { halign: 'left', valign: 'middle' }
      }
    })

    const paymentStartY = (doc as any).lastAutoTable.finalY + 10

    const paymentHeadRow1 = [
      { content: 'Payment / User', rowSpan: 2, styles: { halign: 'left' as const } },
      ...paymentUsers.map((user) => ({
        content: user,
        colSpan: 1,
        styles: { halign: 'center' as const }
      })),
      { content: 'Total', styles: { halign: 'center' as const } }
    ]

    const paymentHeadRow2 = [
      ...paymentUsers.map(() => ({
        content: 'Total',
        styles: { halign: 'center' as const }
      })),
      { content: 'Total', styles: { halign: 'center' as const } }
    ]

    const paymentBody = Array.from(paymentTypes).map((type) => {
      const row = [
        type,
        ...paymentUsers.map((user) => {
          const cell = getPaymentCell(type, user)
          return cell.total ? formatCurrency(cell.total) : '-'
        })
      ]

      row.push(paymentTotals[type] ? formatCurrency(paymentTotals[type]) : '-')

      return row
    })

    const paymentSummaryRow = [
      'Total',
      ...paymentUsers.map((user) => {
        const totalForUser = report.paymentDetails
          .filter((detail) => detail.userName === user)
          .reduce((sum, detail) => sum + detail.paymentValue, 0)

        return totalForUser ? formatCurrency(totalForUser) : '-'
      }),
      report.grandTotalPayment ? formatCurrency(report.grandTotalPayment) : '-'
    ]

    paymentBody.push(paymentSummaryRow)

    autoTable(doc, {
      head: [paymentHeadRow1, paymentHeadRow2],
      body: paymentBody,
      startY: paymentStartY,
      styles: {
        fontSize: 8,
        halign: 'center',
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: [180, 212, 231]
      },
      columnStyles: {
        0: { halign: 'left', valign: 'middle' }
      }
    })

    const pdfBlob = doc.output('blob')
    const blobUrl = URL.createObjectURL(pdfBlob)
    window.open(blobUrl, '_blank')
  }

  const numericCellClass =
    'border border-teal-600 px-3 py-1 text-center align-middle whitespace-nowrap'
  const headerNumericClass =
    'border border-teal-600 px-3 py-1 text-center align-middle whitespace-nowrap font-semibold'
  const textCellClass =
    'border border-teal-600 px-3 py-1 text-left align-middle'
  const renderProductTable = () => {
    if (!report || products.length === 0) {
      return (
        <div className="text-center text-sm text-gray-500 py-6">
          No product data available for the selected filters.
        </div>
      )
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border border-teal-600 text-xs table-auto">
          <thead className="bg-cyan-50">
            <tr>
              <th className={textCellClass} rowSpan={2}>
                Product / User
              </th>
              {productUsers.map((user) => (
                <th
                  key={user}
                  className="border border-teal-600 px-3 py-2 text-center"
                  colSpan={2}
                >
                  {user}
                </th>
              ))}
              <th className="border border-teal-600 px-3 py-2 text-center" colSpan={2}>
                Total
              </th>
            </tr>
            <tr>
              {productUsers.map((user) => (
                <Fragment key={`${user}-sub`}>
                  <th className={headerNumericClass}>
                    Qty
                  </th>
                  <th className={headerNumericClass}>
                    Total
                  </th>
                </Fragment>
              ))}
              <th className={headerNumericClass}>Qty</th>
              <th className={headerNumericClass}>Total</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              const totals = getProductTotals(product)
              return (
                <tr key={product} className="odd:bg-white even:bg-gray-50">
              <td className={`${textCellClass} font-medium`}>
                    {product}
                  </td>
                  {productUsers.map((user) => {
                    const cell = getProductCell(product, user)
                    return (
                      <Fragment key={`${product}-${user}`}>
                        <td className={numericCellClass}>
                          {cell.count || '-'}
                        </td>
                        <td className={numericCellClass}>
                          {cell.total ? formatCurrency(cell.total) : '-'}
                        </td>
                      </Fragment>
                    )
                  })}
                  <td className={`${numericCellClass} font-semibold`}>
                    {totals.count || '-'}
                  </td>
                  <td className={`${numericCellClass} font-semibold`}>
                    {totals.total ? formatCurrency(totals.total) : '-'}
                  </td>
                </tr>
              )
            })}
            <tr className="bg-cyan-100 font-semibold">
              <td className={`${textCellClass} font-semibold`}>Total</td>
              {productUsers.map((user) => {
                const totals = productUserTotals[user]
                return (
                <Fragment key={`total-${user}`}>
                    <td className={numericCellClass}>
                      {totals?.count || '-'}
                    </td>
                    <td className={numericCellClass}>
                      {totals?.total ? formatCurrency(totals.total) : '-'}
                    </td>
                </Fragment>
                )
              })}
              <td className={numericCellClass}>
                {report.grandCount || '-'}
              </td>
              <td className={numericCellClass}>
                {report.grandTotal ? formatCurrency(report.grandTotal) : '-'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  const renderPaymentTable = () => {
    if (!report || paymentTypes.length === 0) {
      return (
        <div className="text-center text-sm text-gray-500 py-6">
          No payment data available for the selected filters.
        </div>
      )
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border border-teal-600 text-xs table-auto">
          <thead className="bg-cyan-50">
            <tr>
              <th className={textCellClass} rowSpan={2}>
                Payment / User
              </th>
              {paymentUsers.map((user) => (
                <th
                  key={user}
                  className="border border-teal-600 px-3 py-2 text-center"
                >
                  {user}
                </th>
              ))}
              <th className="border border-teal-600 px-3 py-2 text-center">Total</th>
            </tr>
            <tr>
              {paymentUsers.map((user) => (
                <th
                  key={`${user}-payment-sub`}
                  className="border border-teal-600 px-2 py-1 text-center"
                >
                  Total
                </th>
              ))}
              <th className="border border-teal-600 px-2 py-1 text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {paymentTypes.map((type) => (
              <tr key={type} className="odd:bg-white even:bg-gray-50">
                <td className={`${textCellClass} font-medium`}>
                  {type}
                </td>
                {paymentUsers.map((user) => {
                  const cell = getPaymentCell(type, user)
                  return (
                    <td
                      key={`${type}-${user}`}
                      className={numericCellClass}
                    >
                      {cell.total ? formatCurrency(cell.total) : '-'}
                    </td>
                  )
                })}
                <td className={`${numericCellClass} font-semibold`}>
                  {paymentTotals[type] ? formatCurrency(paymentTotals[type]) : '-'}
                </td>
              </tr>
            ))}
            <tr className="bg-cyan-100 font-semibold">
              <td className={`${textCellClass} font-semibold`}>Total</td>
              {paymentUsers.map((user) => {
                const totalForUser = report.paymentDetails
                  .filter((detail) => detail.userName === user)
                  .reduce((sum, detail) => sum + detail.paymentValue, 0)

                return (
                  <td
                    key={`payment-total-${user}`}
                    className={numericCellClass}
                  >
                    {totalForUser ? formatCurrency(totalForUser) : '-'}
                  </td>
                )
              })}
              <td className={numericCellClass}>
                {report.grandTotalPayment
                  ? formatCurrency(report.grandTotalPayment)
                  : '-'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="space-y-6 bg-gray-50 min-h-screen p-4 m-5 md:p-6">
      <div className="flex flex-col gap-4 bg-white shadow rounded-lg p-4">
        <div className="flex flex-col gap-2 md:flex-row md:justify-between md:gap-4 items-end">
          <div className="relative" ref={rangePickerRef}>
            <p className="text-sm text-gray-500">Report date range</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleRangeToggle}
                className="inline-flex min-w-[240px] items-center justify-between gap-3 rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-800 shadow-sm hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <span className="font-semibold">{displayDateRange}</span>
                <svg
                  className="h-4 w-4 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleSearch}
                className="inline-flex items-center gap-2 px-4 py-2 rounded border bg-indigo-600 text-sm text-white hover:bg-indigo-700"
              >
                <SearchIcon className="h-4 w-4" />
                Search
              </button>
            </div>

            <DateRangePopover
              isOpen={isRangeOpen}
              presetRanges={presetRanges}
              baseMonth={baseMonth}
              setBaseMonth={setBaseMonth}
              startDate={startDate}
              endDate={endDate}
              onDayClick={handleDayClick}
              setRange={(start, end) => {
                setStartDate(start)
                setEndDate(end)
              }}
            formatDisplayDateProp={formatDisplayDate}
              onClose={() => setIsRangeOpen(false)}
              onSearch={handleSearch}
              isSameDay={isSameDay}
              isBetween={isBetween}
              getMonthMatrix={getMonthMatrix}
            />
          </div>

          <div className="flex flex-1 justify-start md:justify-end">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={!report || isLoading}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded bg-indigo-500 text-white text-sm shadow ${
                !report || isLoading
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <DownloadIcon className="h-4 w-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="border border-red-200 bg-red-50 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      ) : null}

      <section className="space-y-3 bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">
            Product breakdown
          </h2>
          {report ? (
            <p className="text-xs text-gray-500">
              Total Qty: {report.grandCount ?? '-'} | Total Sales:{' '}
              {formatCurrency(report.grandTotal || 0)}
            </p>
          ) : null}
        </div>
        {isLoading ? (
          <div className="text-center py-6 text-sm text-gray-500">
            Loading daily sales report…
          </div>
        ) : (
          renderProductTable()
        )}
      </section>

      <section className="space-y-3 bg-white shadow rounded-lg p-4">
        <h2 className="text-base font-semibold text-gray-800">
          Payment breakdown
        </h2>
        {isLoading ? (
          <div className="text-center py-6 text-sm text-gray-500">
            Loading payment details…
          </div>
        ) : (
          renderPaymentTable()
        )}
      </section>
    </div>
  )
}

export default SalesReportPage
